// Direct PDF export (download, not print) via pdfmake — crisp vector text, real
// page size (A4 by default, or US Letter). Typography echoes the cover: muted
// palette, letter-spaced heads, hairline rules. All rule widths are derived from
// the page geometry so the layout scales correctly to A4 and Letter.

import { saveAs } from "file-saver";
import type { AssembledContract, Block, CoverSpec, PaperSize, SignParty } from "../../contracts/types";
import { signatureBox } from "../../contracts/imageSize";

const INK = "#1a1a1a";
const MUTE = "#6b7280";
const FAINT = "#9aa1ac";
const LINE = "#d3d1c7";
const MARGIN = 48;

function pageW(size: PaperSize): number {
  return size === "LETTER" ? 612 : 595.28;
}
function contentW(size: PaperSize): number {
  return pageW(size) - MARGIN * 2;
}
function rule(width: number, opts: { color?: string; w?: number; top?: number; bottom?: number; x?: number } = {}) {
  const x = opts.x || 0;
  return { canvas: [{ type: "line", x1: x, y1: 0, x2: x + width, y2: 0, lineWidth: opts.w ?? 0.5, lineColor: opts.color || LINE }], margin: [0, opts.top ?? 0, 0, opts.bottom ?? 0] };
}

function coverContent(c: CoverSpec, master: boolean, size: PaperSize, pageBreakBefore: boolean): any[] {
  const cw = contentW(size);
  const items: any[] = [];
  if (pageBreakBefore) items.push({ text: "", pageBreak: "before" });
  items.push({ text: "", margin: [0, master ? 90 : 60, 0, 0] });
  if (c.kicker) items.push({ text: c.kicker.toUpperCase(), alignment: "center", color: MUTE, fontSize: 9, characterSpacing: 3, margin: [0, 0, 0, 10] });
  items.push({ text: c.title, alignment: "center", fontSize: master ? 30 : 22, color: INK, margin: [0, 0, 0, 8] });
  items.push({ ...rule(90, { w: 1.5, color: INK, x: (cw - 90) / 2, bottom: 12 }) });
  if (c.subtitle) items.push({ text: c.subtitle, alignment: "center", italics: true, color: MUTE, fontSize: 12, margin: [60, 0, 60, 8] });

  if (c.contractLabel || c.standardLine || c.forParty || c.preparedBy?.length) {
    items.push({ text: "", margin: [0, 22, 0, 0] });
    if (c.contractLabel) items.push({ text: c.contractLabel.toUpperCase(), alignment: "center", fontSize: 13, bold: true, characterSpacing: 3, margin: [0, 0, 0, 6] });
    if (c.standardLine) items.push({ text: c.standardLine, alignment: "center", fontSize: 10, color: MUTE, characterSpacing: 2, margin: [0, 0, 0, 4] });
    if (c.date) items.push({ text: c.date, alignment: "center", color: MUTE, fontSize: 10 });
    if (c.forParty) {
      items.push({ text: "FOR", alignment: "center", fontSize: 8, color: MUTE, characterSpacing: 2, margin: [0, 18, 0, 4] });
      items.push({ text: c.forParty, alignment: "center", fontSize: 12 });
    }
    if (c.preparedBy?.length) {
      items.push({ text: "PREPARED BY", alignment: "center", fontSize: 8, color: MUTE, characterSpacing: 2, margin: [0, 16, 0, 4] });
      c.preparedBy.forEach((l, i) => items.push({ text: l, alignment: "center", fontSize: i === 0 ? 12 : 10, color: i === 0 ? INK : MUTE }));
    }
    if (c.projectNo) items.push({ text: c.projectNo, alignment: "center", fontSize: 9, color: MUTE, characterSpacing: 2, margin: [0, 16, 0, 0] });
  }

  if (c.meta?.length) {
    items.push({ text: "", margin: [0, 30, 0, 0] });
    items.push({
      columns: [
        { width: "*", text: "" },
        { width: "auto", table: { body: c.meta.map((m) => [{ text: m.label.toUpperCase(), color: MUTE, fontSize: 7.5, characterSpacing: 1, margin: [0, 2, 14, 2] }, { text: m.value, fontSize: 9.5, margin: [0, 2, 0, 2] }]) }, layout: "noBorders" },
        { width: "*", text: "" },
      ],
    });
  }
  return items;
}

const BODY = "#2c2e33";

// Numbered/lettered clause with a hanging indent so wrapped lines align under the
// text, not the marker. Extra indent for originally-indented sub-items.
function clauseNode(raw: string): any {
  const lead = (raw.match(/^(\s*)/)?.[1].length ?? 0) > 0;
  const t = raw.trim();
  const m = t.match(/^(\(?[0-9a-zA-Z]{1,3}[.)])\s+([\s\S]*)$/);
  const indent = lead ? 22 : 0;
  if (m) {
    return {
      columns: [
        { width: 22, text: m[1], color: INK, fontSize: 9, bold: false },
        { width: "*", text: m[2], color: BODY, fontSize: 9, alignment: "left", lineHeight: 1.4 },
      ],
      margin: [indent, 0, 0, 5],
    };
  }
  return { text: t, margin: [indent, 0, 0, 5], color: BODY, fontSize: 9, lineHeight: 1.4 };
}

function blockContent(b: Block, cw: number): any[] {
  switch (b.type) {
    case "heading": {
      if (b.level === 1)
        return [
          { text: b.text, fontSize: 16, bold: true, color: INK, characterSpacing: 0.2, margin: [0, 22, 0, 4] },
          rule(cw, { color: "#b8bcc4", w: 0.75, top: 0, bottom: 11 }),
        ];
      if (b.level === 2) return [{ text: b.text, fontSize: 11.5, bold: true, color: INK, margin: [0, 16, 0, 5] }];
      return [{ text: b.text, fontSize: 9.5, bold: true, color: INK, characterSpacing: 0.3, margin: [0, 12, 0, 3] }];
    }
    case "paragraph":
      return [{ text: b.text, alignment: b.align === "left" ? "left" : "justify", fontSize: 9, color: BODY, margin: [0, 0, 0, 7], lineHeight: 1.42 }];
    case "clause":
      return [clauseNode(b.text)];
    case "list":
      return b.items.map((it) => ({
        columns: [
          { width: 16 + (it.level || 0) * 16, text: it.prefix || "", color: MUTE, fontSize: 9 },
          { width: "*", text: it.text, fontSize: 9, color: BODY, lineHeight: 1.4 },
        ],
        margin: [0, 0, 0, 4],
      }));
    case "table":
      return [
        {
          table: {
            headerRows: 1,
            widths: b.columns.map(() => "*"),
            body: [
              b.columns.map((c) => ({ text: c.toUpperCase(), bold: true, fontSize: 7, color: MUTE, characterSpacing: 0.6 })),
              ...b.rows.map((r) => r.map((v, ci) => ({ text: v, fontSize: 8.5, color: ci === 0 ? INK : BODY }))),
            ],
          },
          layout: {
            hLineWidth: (i: number, node: any) => (i === 0 ? 0 : i === 1 ? 1.2 : i === node.table.body.length ? 1 : 0.5),
            vLineWidth: () => 0,
            hLineColor: (i: number, node: any) => (i === 1 || i === node.table.body.length ? "#8a8f98" : LINE),
            paddingLeft: (i: number) => (i === 0 ? 0 : 10),
            paddingRight: () => 4,
            paddingTop: () => 5.5,
            paddingBottom: () => 5.5,
          },
          margin: [0, 6, 0, 13],
        },
      ];
    case "signblock":
      return [signBlock(b.parties, cw)];
    case "spacer":
      return [{ text: "", margin: [0, 8, 0, 0] }];
    case "pagebreak":
      return [{ text: "", pageBreak: "after" }];
  }
}

function signCol(p: SignParty, lineW: number): any {
  const stack: any[] = [];
  if (p.caption) stack.push({ text: p.caption, fontSize: 8, color: MUTE, margin: [0, 0, 0, 3] });
  stack.push({ text: `Signed for and on behalf of the ${p.role}`, bold: true, fontSize: 9, margin: [0, 0, 0, 10] });
  const field = (label: string, value?: string) => [
    { text: label.toUpperCase(), fontSize: 7, color: MUTE, characterSpacing: 0.5, margin: [0, 8, 0, 1] },
    { text: value || " ", fontSize: 9.5 },
  ];
  stack.push(...field("Name", p.name), ...field("Title", p.title));
  stack.push({ text: "SIGNATURE", fontSize: 7, color: MUTE, characterSpacing: 0.5, margin: [0, 10, 0, 2] });
  if (p.signatureDataUri) {
    const box = signatureBox(p.signatureDataUri, Math.min(190, lineW), 54);
    stack.push({ image: p.signatureDataUri, width: box.width, height: box.height, margin: [0, 2, 0, 2] });
  }
  stack.push(rule(lineW, { color: INK, w: 0.7, top: p.signatureDataUri ? 2 : 8 }));
  stack.push({ text: "DATE", fontSize: 7, color: MUTE, characterSpacing: 0.5, margin: [0, 10, 0, 2] });
  if (p.signedDate) stack.push({ text: p.signedDate, fontSize: 9.5, color: INK, margin: [0, 2, 0, 2] });
  stack.push(rule(lineW, { color: INK, w: 0.7, top: p.signedDate ? 2 : 8 }));
  if (p.witnessName !== undefined || p.witnessAddress !== undefined) {
    stack.push({ text: "IN THE PRESENCE OF (WITNESS)", fontSize: 7, color: MUTE, characterSpacing: 0.5, margin: [0, 12, 0, 4] });
    stack.push(...field("Name", p.witnessName), ...field("Address", p.witnessAddress));
  }
  return { width: "*", stack };
}
function signBlock(parties: SignParty[], cw: number): any {
  const gap = 28;
  const lineW = Math.max(120, (cw - gap) / 2 - 12);
  return { stack: [{ text: "EXECUTION", fontSize: 7.5, color: MUTE, characterSpacing: 1.5, margin: [0, 14, 0, 10] }, { columns: parties.map((p) => signCol(p, lineW)), columnGap: gap }], margin: [0, 12, 0, 0] };
}

// Render blocks. We keep a heading glued to the block that follows it (via
// pdfmake's headlineLevel, which never drops content — unlike `unbreakable`,
// which silently drops wrapped tables/columns) so headings don't orphan at a
// page foot. Tables and the signature block flow naturally and are never wrapped.
function pushBlocksCoherent(content: any[], blocks: Block[], cw: number): void {
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    const nodes = blockContent(b, cw);
    // A heading immediately followed by content keeps with it (headlineLevel groups
    // adjacent nodes sharing the level so a page break won't split them apart).
    if (b.type === "heading" && b.level >= 2 && blocks[i + 1] && blocks[i + 1].type !== "heading") {
      for (const nnode of nodes) if (nnode && typeof nnode === "object") nnode.headlineLevel = i + 1;
      const nextNodes = blockContent(blocks[i + 1], cw);
      if (nextNodes[0] && typeof nextNodes[0] === "object") nextNodes[0].headlineLevel = i + 1;
      content.push(...nodes, ...nextNodes);
      i++;
      continue;
    }
    content.push(...nodes);
  }
}

function buildDocDefinition(contract: AssembledContract, size: PaperSize): any {
  const cw = contentW(size);
  const pw = pageW(size);
  const content: any[] = [];
  // Short, single-part documents (payment certificates, IPCs) skip the Contents
  // page and the redundant part cover — cover, then the certificate itself.
  const single = contract.parts.length === 1;
  if (!contract.noCover) content.push(...coverContent(contract.masterCover, true, size, false));

  if (!single) {
    content.push({ text: "", pageBreak: "before" });
    content.push({ text: "Contents", fontSize: 18, color: INK, margin: [0, 10, 0, 5] });
    content.push(rule(56, { color: INK, w: 1.5, bottom: 12 }));
    contract.parts.forEach((p, i) => content.push({ columns: [{ width: 20, text: `${i + 1}.`, color: MUTE, fontSize: 9 }, { width: "*", text: p.name, fontSize: 9.5 }], margin: [0, 4, 0, 4] }));
  }

  contract.parts.forEach((part) => {
    if (!single) content.push(...coverContent(part.cover, false, size, true));
    // Only break if something precedes this part — a letter has no cover or
    // contents, so an unconditional break would open on a blank page.
    if (content.length) content.push({ text: "", pageBreak: "before" });
    pushBlocksCoherent(content, part.blocks, cw);
  });

  content.push(rule(cw, { color: LINE, w: 0.5, top: 16, bottom: 8 }));
  content.push({ text: contract.notice, italics: true, fontSize: 7.5, color: MUTE, lineHeight: 1.3 });

  return {
    pageSize: size,
    pageMargins: [MARGIN, 52, MARGIN, 48],
    info: { title: contract.title },
    defaultStyle: { font: "Roboto", fontSize: 9, color: BODY, lineHeight: 1.4 },
    header: (currentPage: number) =>
      currentPage === 1 ? undefined : { text: `${contract.title}  ·  ${contract.standardLabel.split("—")[0].trim()}`, alignment: "right", color: FAINT, fontSize: 7, margin: [MARGIN, 26, MARGIN, 0] },
    footer: (currentPage: number, pageCount: number) => ({
      stack: [
        { canvas: [{ type: "line", x1: MARGIN, y1: 0, x2: pw - MARGIN, y2: 0, lineWidth: 0.5, lineColor: LINE }] },
        { text: `Page ${currentPage} of ${pageCount}`, alignment: "center", color: MUTE, fontSize: 7, margin: [0, 4, 0, 0] },
      ],
    }),
    content,
  };
}

// pdfmake's vfs_fonts.js is a bare browser script (`this.pdfMake = this.pdfMake
// || {}`). Imported as a strict ESM module its top-level `this` is undefined and
// it throws. So we load it as raw source and run it with a controlled `this`
// (functions built via `new Function` are non-strict → `this` is the given
// object), then read the vfs off it. Cached after first load.
let cachedVfs: Record<string, string> | null = null;
async function loadVfs(): Promise<Record<string, string>> {
  if (cachedVfs) return cachedVfs;
  const mod: any = await import("pdfmake/build/vfs_fonts.js?raw");
  const src: string = mod.default || mod;
  const scope: any = {};
  // eslint-disable-next-line no-new-func
  const run = new Function(src + "\n;return this.pdfMake && this.pdfMake.vfs;");
  cachedVfs = run.call(scope);
  return cachedVfs!;
}

// Render the contract to the exact output PDF blob (same document definition the
// download uses). Shared by the download and the conflict scanner, so the scanner
// reviews precisely what gets exported.
export async function renderPdfBlob(contract: AssembledContract, size: PaperSize = "A4"): Promise<Blob> {
  const pdfMakeMod: any = await import("pdfmake/build/pdfmake");
  const pdfMake = pdfMakeMod.default || pdfMakeMod;
  pdfMake.vfs = await loadVfs();
  if (!pdfMake.vfs || !Object.keys(pdfMake.vfs).length) throw new Error("PDF fonts failed to load.");
  return await new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(buildDocDefinition(contract, size)).getBlob((b: Blob) => resolve(b));
    } catch (e) {
      reject(e);
    }
  });
}

export async function exportPdf(contract: AssembledContract, size: PaperSize = "A4"): Promise<void> {
  const safe = contract.title.replace(/[^\w\d]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60) || "contract";
  // Generate the blob and download it via file-saver (the same reliable path as
  // the .docx export) rather than pdfmake's internal, browser-fragile download().
  const blob = await renderPdfBlob(contract, size);
  saveAs(blob, `${safe}.pdf`);
}
